import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useHomepageData, type HomepageTopic } from '@/hooks/useHomepageData';
import { useCountUp } from '@/hooks/useCountUp';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/lib/auth/AuthContext';
import type { CarouselEpisode } from './CarouselEpisodeCard';
import { SEO } from './SEO';
import { EpisodeCarousel } from './EpisodeCarousel';
import { ActionButtons } from './ActionButtons';

export function Homepage() {
  const { topics, episodes: recentEpisodes, episodesCount, isLoading } = useHomepageData();
  const { isAuthenticated } = useAuth();
  const {
    isTopicFollowed,
    isTopicPending,
    toggleTopicFollow,
    isEpisodeLiked,
    isEpisodePending,
    toggleEpisodeLike,
  } = useFavorites();

  // Create topic slug -> name mapping for episodes
  const topicNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const topic of topics) {
      map.set(topic.slug, topic.name);
    }
    return map;
  }, [topics]);

  // Sort topics by latest episode publishedAt descending (newest first, nulls at end)
  const sortedTopics = useMemo(() => {
    return [...topics].sort((a, b) => {
      const aLatest = a.latestEpisode.edges[0]?.node.publishedAt;
      const bLatest = b.latestEpisode.edges[0]?.node.publishedAt;
      // Null values go to the end
      if (!aLatest && !bLatest) return 0;
      if (!aLatest) return 1;
      if (!bLatest) return -1;
      // Sort by date descending (newest first)
      return new Date(bLatest).getTime() - new Date(aLatest).getTime();
    });
  }, [topics]);

  // Transform recent episodes to carousel format with topic names
  const carouselEpisodes: CarouselEpisode[] = useMemo(() => {
    return recentEpisodes.slice(0, 10).map((ep) => ({
      topicSlug: ep.topicSlug,
      topicName: topicNameMap.get(ep.topicSlug) ?? ep.topicSlug,
      episodeId: ep.episodeId,
      title: ep.title,
      summary: ep.summary,
      audioS3Key: ep.audioS3Key,
      durationSeconds: ep.durationSeconds,
      publishedAt: ep.publishedAt,
    }));
  }, [recentEpisodes, topicNameMap]);

  // Use totalCount from episodes query (more accurate)
  const totalEpisodes = episodesCount;

  // Animated counters - pass undefined while loading to show placeholder
  const animatedTopicCount = useCountUp(isLoading ? undefined : sortedTopics.length);
  const animatedEpisodeCount = useCountUp(isLoading ? undefined : totalEpisodes);

  // Search state
  const [topicSearch, setTopicSearch] = useState('');

  // Fuse.js for topic search
  const topicFuse = useMemo(() => new Fuse<HomepageTopic>(sortedTopics, {
    keys: ['name', 'slug'],
    threshold: 0.4,
  }), [sortedTopics]);

  // Filtered results
  const filteredTopics = topicSearch
    ? topicFuse.search(topicSearch).map(r => r.item)
    : sortedTopics;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
      <SEO
        description="AI-powered podcast generation platform. Fresh episodes generated automatically by crawling thousands of news sources across the web."
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden px-6 pb-20 pt-24">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-3xl" />
          <div className="absolute top-20 left-20 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="absolute top-40 right-20 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-4 py-2 text-sm text-indigo-300">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-500" />
            </span>
            AI-Powered Content
          </div>

          <h1 className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-5xl font-bold tracking-tight text-transparent sm:text-6xl">
            Podcasts that create
            <br />
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text">themselves</span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-400">
            Fresh podcast episodes generated automatically by crawling thousands of news sources across the web.
            Stay informed on any topic with AI-curated audio briefings delivered on your schedule.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/dashboard"
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3.5 font-medium text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30"
            >
              {isAuthenticated ? 'Dashboard' : 'Sign In'}
              <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative border-y border-gray-800 bg-gray-900/50 px-6 py-12 backdrop-blur-sm">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-8 sm:gap-16">
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums text-white">
              {animatedTopicCount ?? '–'}
            </div>
            <div className="mt-1 text-sm font-medium uppercase tracking-wider text-gray-500">Active Podcasts</div>
          </div>
          <div className="hidden h-12 w-px bg-gray-800 sm:block" />
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums text-white">
              {animatedEpisodeCount ?? '–'}
            </div>
            <div className="mt-1 text-sm font-medium uppercase tracking-wider text-gray-500">Total Episodes</div>
          </div>
        </div>
      </section>

      {/* Latest Episodes Carousel */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-white">Latest Episodes</h2>
          <p className="mt-3 text-gray-400">Fresh content from across all podcasts</p>
        </div>
        <EpisodeCarousel
          episodes={carouselEpisodes}
          isLoading={isLoading}
          isAuthenticated={isAuthenticated}
          isEpisodeLiked={isEpisodeLiked}
          isEpisodePending={isEpisodePending}
          toggleEpisodeLike={toggleEpisodeLike}
        />
      </section>

      {/* Podcasts Grid */}
      <main id="podcasts" className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-white">Browse Podcasts</h2>
          <p className="mt-3 text-gray-400">Subscribe to topics that interest you</p>
        </div>

        {/* Topic Search */}
        <div className="mx-auto mb-8 max-w-md">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search podcasts..."
              value={topicSearch}
              onChange={(e) => setTopicSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-700 bg-gray-800/50 py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        ) : filteredTopics.length === 0 ? (
          <div className="mx-auto max-w-md rounded-2xl border border-gray-800 bg-gray-900/50 p-12 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
              <svg className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" role="img" aria-label="Microphone off">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white">{topicSearch ? 'No matching podcasts' : 'No podcasts yet'}</h3>
            <p className="mt-2 text-gray-400">
              {topicSearch ? 'Try a different search term' : "We're working on creating amazing content. Check back soon for new podcasts!"}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredTopics.map((topic) => (
              <Link
                key={topic.slug}
                to={`/p/${topic.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-6 transition-all duration-300 hover:border-gray-700 hover:shadow-xl hover:shadow-indigo-500/5"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

                <div className="relative">
                  <div className="mb-5 flex items-start justify-between">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 ring-1 ring-white/10">
                      <svg className="h-7 w-7 text-indigo-400" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Headphones">
                        <path d="M12 3C7.03 3 3 7.03 3 12v7c0 1.1.9 2 2 2h2v-8H5v-1c0-3.87 3.13-7 7-7s7 3.13 7 7v1h-2v8h2c1.1 0 2-.9 2-2v-7c0-4.97-4.03-9-9-9zM7 15v4H5v-4h2zm12 4h-2v-4h2v4z"/>
                      </svg>
                    </div>
                    <ActionButtons
                      isFavorited={isTopicFollowed(topic.slug)}
                      isPending={isTopicPending(topic.slug)}
                      onToggleFavorite={() => toggleTopicFollow(topic.slug)}
                      shareUrl={`${window.location.origin}/p/${topic.slug}`}
                      shareTitle={topic.name}
                      showFavorite={isAuthenticated}
                      size="sm"
                    />
                  </div>

                  <h3 className="text-lg font-semibold text-white transition-colors group-hover:text-indigo-300">
                    {topic.name}
                  </h3>

                  <div className="mt-4 flex items-center gap-3 text-sm text-gray-500">
                    {topic.episodeCount > 0 ? (
                      <>
                        <span>
                          {topic.episodeCount} {topic.episodeCount === 1 ? 'episode' : 'episodes'}
                        </span>
                        {topic.latestEpisode.edges[0]?.node.publishedAt && (
                          <span className="flex items-center gap-1">
                            <span className="text-gray-600">·</span>
                            {new Date(topic.latestEpisode.edges[0].node.publishedAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                      </>
                    ) : (
                      <span>Coming soon</span>
                    )}
                  </div>

                  <div className="mt-4 flex items-center text-sm font-medium text-indigo-400 opacity-0 transition-opacity group-hover:opacity-100">
                    View all episodes
                    <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" role="img" aria-label="Arrow right">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-10">
        <div className="mx-auto max-w-7xl text-center">
          <div className="mb-3 flex items-center justify-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
              <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Microphone">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
            </div>
            <span className="font-medium text-white">Kaster Media</span>
          </div>
          <p className="text-sm text-gray-500">
            Built with ❤️ by Kaster Media
          </p>
        </div>
      </footer>
    </div>
  );
}
